import './DeviceStatusDetails.scss';

import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Map } from 'immutable';
import { useSelector } from 'react-redux';
import Grid from '@mui/material/Grid';
import { Paper } from '@mui/material';
import { useTranslation, hasTranslation } from 'src/i18n';
import MajorStat from 'src/component/UI/Statistics/MajorStat';
import { getTemperatureUnit } from 'src/module/authentication/selector';
import { celsiusToFahrenheitConvertor } from 'src/utils/utils';
import { matchStatusField, captureVariables, flattenEntries } from './statusFields';

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'string') {
    return _.upperFirst(value);
  }
  return value;
};

export default function DeviceStatusDetails (props) {
  const { deviceStatus, layoutConfig } = props;
  const { t } = useTranslation();
  const temperatureUnit = useSelector(getTemperatureUnit);

  // The device factory describes each status reading (label/unit/omit) via
  // layoutConfig.status.fields; everything else here just renders what it says.
  const rawStatusFields = layoutConfig?.getIn?.(['status', 'fields']);
  const statusFields = rawStatusFields?.toJS ? rawStatusFields.toJS() : (rawStatusFields ?? {});

  // Render the display value with its unit. Temperatures arrive in Celsius and
  // convert to the user's preferred unit; currents are always amps.
  const withUnit = (display, unit) => (
    <>{formatValue(display)}<span className='device-status-details__unit'>{unit}</span></>
  );
  const resolveStat = (field, value) => {
    if (typeof value === 'number' && field?.unit === 'temperature') {
      const display = temperatureUnit === 'F' ? celsiusToFahrenheitConvertor(value) : value;
      return withUnit(display, temperatureUnit === 'F' ? '°F' : '°C');
    }
    if (typeof value === 'number' && field?.unit === 'current') {
      return withUnit(value, 'A');
    }
    return formatValue(value);
  };

  // Resolve a label for a flattened status key. The factory-supplied label is an
  // i18n key (interpolating the matched `*` segments); fall back to the status
  // key's own translation, then to a humanized key for anything undescribed.
  const resolveLabel = (key, field, captures) => {
    const variables = captureVariables(captures);
    if (field?.label && hasTranslation(field.label)) { return t(field.label, variables); }
    if (hasTranslation(key)) { return t(key, variables); }
    if (hasTranslation(`gauge.${key}`)) { return t(`gauge.${key}`); }
    return _.startCase(key.replace(/\./g, ' '));
  };

  // Flatten the status (including nested objects like temperatureSensors), resolve
  // each reading's descriptor, and drop the ones the factory marks as omitted.
  const status = (deviceStatus || Map()).toJS();
  const entries = flattenEntries(status)
    .map(([key, value]) => ({ key, value, ...matchStatusField(key, statusFields) }))
    .filter(({ field }) => !field?.omit);

  if (!entries.length) {
    return null;
  }

  return (
    <Paper className='device-status-details'>
      <h3>{t('common.status')}</h3>
      <Grid container spacing={3}>
        {entries.map(({ key, value, field, captures }) => (
          <Grid item key={key} xs={6} sm={4} md={3}>
            <MajorStat title={resolveLabel(key, field, captures)} stat={resolveStat(field, value)} style={{ title: { textAlign: 'center' } }} />
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}

DeviceStatusDetails.propTypes = {
  deviceStatus: PropTypes.object,
  layoutConfig: PropTypes.object
};
